import { Button, ButtonColor, ButtonSize } from "@swivl-mono/ui";
import { ReactNode } from "react";
import { BiEditAlt } from "react-icons/bi";

interface ScheduleCardProps {
  title: string;
  caption?: ReactNode;
  /** Inheritance tag and any revert action, shown beside the title. */
  status?: ReactNode;
  /** Header buttons — usually `<CardEditAction>` or `<CardSaveActions>`. */
  actions?: ReactNode;
  error?: string | null;
  loading?: boolean;
  loadingLabel?: string;
  children: ReactNode;
}

/**
 * Card chrome for every section of the Hours page, matching the Facility Info
 * cards: title and caption on the left, edit affordance on the right, content
 * below a divider.
 *
 * The card is deliberately dumb about editing. Hours sections don't share a
 * save model — a weekly grid stages one draft and saves it, while the holiday
 * list saves each rule as it's added — so each section supplies its own header
 * buttons rather than the card imposing one shape on all of them.
 */
export function ScheduleCard({ title, caption, status, actions, error, loading, loadingLabel, children }: ScheduleCardProps): React.ReactElement {
  return (
    <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="heading-base-alt text-grey-900">{title}</h3>
            {status}
          </div>
          {caption && <p className="body-sm mt-0.5 text-grey-600">{caption}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>

      <div className="border-t border-grey-200">
        {error && <p className="body-sm mx-4 my-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-red-700">{error}</p>}
        {loading ? <p className="body-sm px-4 py-3 text-grey-500">{loadingLabel ?? "Loading…"}</p> : children}
      </div>
    </section>
  );
}

/** The resting-state Edit affordance, matching the other Details v3 pages. */
export function CardEditAction({ onEdit, label = "Edit" }: { onEdit: () => void; label?: string }): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onEdit}
      className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
    >
      {label}
      <BiEditAlt className="size-4" />
    </button>
  );
}

interface CardSaveActionsProps {
  saving: boolean;
  /** Nothing staged yet — Save stays disabled so it can't write a no-op row. */
  canSave?: boolean;
  onCancel: () => void;
  onSave: () => Promise<void>;
}

/** Cancel + Save pair for sections that stage a draft before writing. */
export function CardSaveActions({ saving, canSave = true, onCancel, onSave }: CardSaveActionsProps): React.ReactElement {
  return (
    <>
      <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={saving} onClick={onCancel}>
        Cancel
      </Button>
      <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} disabled={saving || !canSave} onClickAsync={onSave}>
        {saving ? "Saving…" : "Save"}
      </Button>
    </>
  );
}
